/**
 * Serviço simplificado para busca direta de propriedades sem polling
 * Realiza chamadas diretas à API e processa GeoJSON para uso imediato
 */
import { propertyApi, formatErrorMessage } from './BaseApiService';
import GeometryService from './GeometryService.js';

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function assertArray(value, fieldName) {
  if (!Array.isArray(value)) {
    throw new Error(`[PropertyService] Contrato invalido: campo '${fieldName}' deve ser array`);
  }
}

function normalizeDomainKeys(props) {
  if (!isPlainObject(props)) {
    throw new Error("[PropertyService] Contrato invalido: campo 'gn_properties' deve ser objeto");
  }

  const out = {};
  for (const key of Object.keys(props)) {
    const lowerKey = typeof key === 'string' ? key.toLowerCase() : key;
    out[lowerKey] = props[key];
  }
  return out;
}

function normalizeCarDetails(properties) {
  if (!Array.isArray(properties.car)) return;

  properties.car.forEach((carItem) => {
    if (!carItem || !Array.isArray(carItem.details)) return;
    carItem._detail = carItem.details[0] ?? null;
  });
}

function normalizeEntidades(properties) {
  if (properties.entidades === undefined || properties.entidades === null) {
    properties.entidades = [];
    return;
  }

  if (!Array.isArray(properties.entidades)) {
    throw new Error(
      "[PropertyService] Contrato invalido: campo 'gn_properties.entidades' deve ser array",
    );
  }

  const flattened = [];

  properties.entidades.forEach((entidadeEntry) => {
    if (!isPlainObject(entidadeEntry)) {
      throw new Error(
        "[PropertyService] Contrato invalido: item de 'gn_properties.entidades' deve ser objeto",
      );
    }

    assertArray(entidadeEntry.pf, 'gn_properties.entidades[].pf');
    assertArray(entidadeEntry.pj, 'gn_properties.entidades[].pj');

    entidadeEntry.pf.forEach((pfEntry) => {
      if (!isPlainObject(pfEntry)) {
        throw new Error(
          "[PropertyService] Contrato invalido: item de 'gn_properties.entidades[].pf' deve ser objeto",
        );
      }
      const situacaoPf = Array.isArray(pfEntry.situacao)
        ? pfEntry.situacao.find((item) => isPlainObject(item)) || null
        : isPlainObject(pfEntry.situacao)
          ? pfEntry.situacao
          : null;

      flattened.push({
        ...pfEntry,
        tipo: 'PF',
        tipo_documento: pfEntry.tipo_documento || 'CPF',
        documento: pfEntry.documento || pfEntry.ni || null,
        nome: pfEntry.nome || null,
        situacao_codigo: pfEntry.situacao_codigo || situacaoPf?.codigo || null,
        situacao_descricao: pfEntry.situacao_descricao || situacaoPf?.descricao || null,
        data_inscricao: pfEntry.data_inscricao || pfEntry.datainscricao || null,
      });
    });

    entidadeEntry.pj.forEach((pjEntry) => {
      if (!isPlainObject(pjEntry)) {
        throw new Error(
          "[PropertyService] Contrato invalido: item de 'gn_properties.entidades[].pj' deve ser objeto",
        );
      }

      const situacaoPj = Array.isArray(pjEntry.situacaoCadastral)
        ? pjEntry.situacaoCadastral.find((item) => isPlainObject(item)) || null
        : isPlainObject(pjEntry.situacaoCadastral)
          ? pjEntry.situacaoCadastral
          : null;

      const naturezaJuridica = Array.isArray(pjEntry.naturezaJuridica)
        ? pjEntry.naturezaJuridica.find((item) => isPlainObject(item)) || null
        : isPlainObject(pjEntry.naturezaJuridica)
          ? pjEntry.naturezaJuridica
          : null;

      flattened.push({
        ...pjEntry,
        tipo: 'PJ',
        tipo_documento: pjEntry.tipo_documento || 'CNPJ',
        documento: pjEntry.documento || pjEntry.ni || null,
        nome: pjEntry.nome || pjEntry.nomeEmpresarial || pjEntry.nomeFantasia || null,
        situacao_codigo: pjEntry.situacao_codigo || situacaoPj?.codigo || null,
        situacao_descricao: pjEntry.situacao_descricao || situacaoPj?.motivo || null,
        natureza_juridica: pjEntry.natureza_juridica || naturezaJuridica || null,
      });
    });
  });

  properties.entidades = flattened;
}

function normalizeCcir(properties) {
  if (properties.serpro_ccir === undefined || properties.serpro_ccir === null) {
    properties.serpro_ccir = [];
    return;
  }

  if (!Array.isArray(properties.serpro_ccir)) {
    throw new Error(
      "[PropertyService] Contrato invalido: campo 'gn_properties.serpro_ccir' deve ser array",
    );
  }

  properties.serpro_ccir = properties.serpro_ccir.map((ccirEntry) => {
    if (!isPlainObject(ccirEntry)) {
      throw new Error(
        "[PropertyService] Contrato invalido: item de 'gn_properties.serpro_ccir' deve ser objeto",
      );
    }

    const dadosUltimoRaw = ccirEntry.dados_ultimo_ccir;
    let dadosUltimoList = [];

    if (Array.isArray(dadosUltimoRaw)) {
      dadosUltimoList = dadosUltimoRaw;
    } else if (isPlainObject(dadosUltimoRaw)) {
      dadosUltimoList = [dadosUltimoRaw];
    }

    const dadosUltimoCcirFirst = dadosUltimoList.find((item) => isPlainObject(item)) || null;

    return {
      ...ccirEntry,
      dados_ultimo_ccir: dadosUltimoCcirFirst,
      dados_ultimo_ccir_first: dadosUltimoCcirFirst,
      dados_ultimo_ccir_list: dadosUltimoList,
    };
  });
}

function parseGeojsonInDomains(properties) {
  if (Array.isArray(properties.car)) {
    properties.car.forEach((carItem) => {
      if (!carItem || !Array.isArray(carItem.themes)) return;

      carItem.themes.forEach((theme) => {
        if (!theme || !theme.geojson || typeof theme.geojson !== 'string') return;
        try {
          theme.geojson = JSON.parse(theme.geojson);
        } catch (_) {
          // GeoJSON inválido não deve bloquear todo o payload; camada será ignorada no GeometryService
        }
      });
    });
  }

  if (Array.isArray(properties.sigef)) {
    properties.sigef.forEach((sigefItem) => {
      if (!sigefItem || !Array.isArray(sigefItem.parcels)) return;

      sigefItem.parcels.forEach((parcel) => {
        if (!parcel || !parcel.geojson || typeof parcel.geojson !== 'string') return;
        try {
          parcel.geojson = JSON.parse(parcel.geojson);
        } catch (_) {
          // GeoJSON inválido não deve bloquear todo o payload; camada será ignorada no GeometryService
        }
      });
    });
  }
}

function resolveSearchPayloadEnvelope(rawPayload) {
  if (!isPlainObject(rawPayload)) return null;

  if (isPlainObject(rawPayload.gn_properties)) {
    return {
      envelope: rawPayload,
      payload: rawPayload,
      requestStatus: rawPayload.status,
    };
  }

  if (isPlainObject(rawPayload.data) && isPlainObject(rawPayload.data.gn_properties)) {
    return {
      envelope: rawPayload,
      payload: rawPayload.data,
      requestStatus: rawPayload.status,
    };
  }

  return null;
}

function hasResolvedPropertyPayload(rawPayload) {
  return resolveSearchPayloadEnvelope(rawPayload) !== null;
}

function adaptPropertyPayloadStrict(responseData) {
  const resolved = resolveSearchPayloadEnvelope(responseData);
  if (!resolved) {
    throw new Error(
      '[PropertyService] Contrato invalido: payload da propriedade ausente ou invalido',
    );
  }

  const { envelope, payload, requestStatus } = resolved;

  if (!isPlainObject(payload.gn_properties)) {
    throw new Error(
      "[PropertyService] Contrato invalido: campo obrigatorio 'gn_properties' ausente",
    );
  }

  const metadataRaw = payload._metadata;
  const metadata = Array.isArray(metadataRaw)
    ? metadataRaw.find((item) => isPlainObject(item)) || null
    : isPlainObject(metadataRaw)
      ? metadataRaw
      : null;

  if (!isPlainObject(metadata)) {
    throw new Error("[PropertyService] Contrato invalido: campo '_metadata' ausente ou invalido");
  }

  const timelineSource = payload.status ?? requestStatus;
  const statusTimeline = Array.isArray(timelineSource)
    ? timelineSource
    : timelineSource !== undefined && timelineSource !== null
      ? [timelineSource]
      : [];

  const properties = normalizeDomainKeys(payload.gn_properties);
  normalizeCarDetails(properties);
  normalizeEntidades(properties);
  normalizeCcir(properties);
  parseGeojsonInDomains(properties);

  return {
    ...envelope,
    ...payload,
    properties,
    _metadata: metadata,
    _metadata_list: Array.isArray(metadataRaw) ? metadataRaw : [metadata],
    status_timeline: statusTimeline,
  };
}

/**
 * Busca propriedade por coordenada usando o endpoint unificado
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {Function} setPropertyData - Função para definir os dados da propriedade no estado
 * @param {Function} setLoading - Função para definir o estado de carregamento
 * @param {Function} handlePropertyResult - Handler de processamento opcional
 * @returns {Promise<Object>} Dados da propriedade encontrada
 */
export async function buscarPorCoordenada(
  latitude,
  longitude,
  setPropertyData,
  setLoading,
  handlePropertyResult,
  extraParams = {},
) {
  try {
    if (setLoading) setLoading(true);

    // Prepara os parâmetros da busca
    const params = { latitude, longitude, ...(extraParams || {}) };

    // Delega para a função unificada, passando tipo 'coordenada' para logs específicos
    return await buscarPropriedade(
      params,
      setPropertyData,
      setLoading,
      handlePropertyResult,
      'coordenada',
    );
  } catch (error) {
    //console.error('[PropertyService] Erro ao buscar propriedade por coordenada:', error);
    if (setLoading) setLoading(false);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error));
  }
}

/**
 * Busca propriedade por código (CAR, SIGEF, Parcela)
 * @param {Object|string} params - Objeto contendo o código da propriedade ou string com o código
 * @param {Function} setPropertyData - Função para definir o estado de dados da propriedade
 * @param {Function} setLoading - Função para definir o estado de carregamento
 * @param {Function} handlePropertyResult - Função opcional para processar os resultados
 * @returns {Promise<Object>} Dados da propriedade encontrada
 */
export async function buscarPorCodigo(params, setPropertyData, setLoading, handlePropertyResult) {
  try {
    if (setLoading) setLoading(true);

    // Normaliza parâmetros: strings tornam-se objetos com o código apropriado
    let searchParams = {};

    // Verifica se params é uma string (compatibilidade com versão anterior)
    if (typeof params === 'string') {
      const codigo = params;

      // Usando o formato de código para determinar o parâmetro a ser enviado
      if (/^[A-Z]{2}-\d{7}/.test(codigo)) {
        // Formato CAR: XX-DDDDDDD-...
        searchParams.cod_imovel = codigo;
      } else if (/^\d{13}$/.test(codigo)) {
        // Formato SIGEF: 13 dígitos numéricos
        searchParams.codigo_imo = codigo;
      } else {
        // Assume formato de parcela caso não seja nem CAR nem SIGEF
        searchParams.parcela_co = codigo;
      }
    } else if (params && typeof params === 'object') {
      // Usa o objeto diretamente
      searchParams = { ...params };
    }

    // Delega para a função unificada, passando tipo 'codigo' para logs específicos
    return await buscarPropriedade(
      searchParams,
      setPropertyData,
      setLoading,
      handlePropertyResult,
      'codigo',
    );
  } catch (error) {
    //console.error('[PropertyService] Erro ao buscar propriedade por código:', error);
    if (setLoading) setLoading(false);
    // eslint-disable-next-line preserve-caught-error
    throw new Error(formatErrorMessage(error));
  }
}

/**
 * Função unificada para buscar propriedade por qualquer parâmetro e processar o resultado
 * @param {Object} params - Parâmetros de busca (latitude/longitude ou códigos)
 * @param {Function} setPropertyData - Função para definir os dados da propriedade no estado
 * @param {Function} setLoading - Função para definir o estado de carregamento
 * @param {Function} handlePropertyResult - Handler de processamento opcional
 * @param {string} tipoConsulta - Tipo de consulta ('codigo' ou 'coordenada') para logs
 * @returns {Promise<Object>} Dados da propriedade encontrada e processada
 */
async function buscarPropriedade(
  params,
  setPropertyData,
  setLoading,
  handlePropertyResult,
  tipoConsulta,
) {
  try {
    const __DEBUG = import.meta.env.VITE_DEBUG === 'true';
    // eslint-disable-next-line no-console
    if (__DEBUG) console.time('[Perf] Total.search->render');
    // Verificação básica dos parâmetros
    if (!params || Object.keys(params).length === 0) {
      throw new Error('É necessário fornecer parâmetros válidos para busca');
    }

    // Usando o endpoint unificado
    // eslint-disable-next-line no-console
    if (__DEBUG) console.time('[Perf] Search.request');
    const response = await propertyApi.post('/api/v1/search', params);
    // eslint-disable-next-line no-console
    if (__DEBUG) console.timeEnd('[Perf] Search.request');

    // Log COMPLETO da resposta bruta do backend
    //console.log('**** RESPOSTA BRUTA DO BACKEND ****');
    //console.log('[PropertyService] URL da requisição:', `${PROPERTY_SERVICE_URL}/api/v1/search`);
    //console.log('[PropertyService] Parâmetros enviados:', params);
    //console.log('[PropertyService] Status da resposta:', response?.status, response?.statusText);
    //console.log('[PropertyService] Headers da resposta:', response?.headers);
    //console.log('[PropertyService] RESPOSTA COMPLETA DO BACKEND (JSON):', response.data);

    // Log resumido para referência
    //console.log('[PropertyService] Resposta raw da API (resumo):', {
    //  status: response?.status,
    //  statusText: response?.statusText,
    //  headers: response?.headers,
    //  dataKeys: response.data ? Object.keys(response.data) : [],
    //  dataPreview: response.data ? JSON.stringify(response.data).substring(0, 300) + '...' : 'Sem dados'
    //});

    // Validar resposta
    if (!response.data) {
      throw new Error('Resposta da API sem dados');
    }

    const responseData = response.data;
    const acceptedStatus =
      isPlainObject(responseData) &&
      responseData.status !== undefined &&
      responseData.status !== null
        ? String(responseData.status).toLowerCase()
        : null;
    const acceptedRequestId = isPlainObject(responseData) ? responseData.request_id || null : null;
    const acceptedJobId = isPlainObject(responseData) ? responseData.job_id || null : null;
    const acceptedFileId = isPlainObject(responseData) ? responseData.file_id || null : null;

    // Verifica se o content-type da resposta é realmente JSON
    const contentType = response.headers['content-type'];
    if (!contentType || !contentType.includes('application/json')) {
      //console.error(`[PropertyService] Tipo inesperado de conteúdo na resposta: ${contentType}`);
      throw new Error(`Resposta inesperada do servidor: ${contentType}`);
    }

    // Extrai e processa os dados da propriedade
    // Contrato assíncrono: /search pode retornar apenas aceite com file_id/job_id.
    // Nesse caso, buscamos o snapshot em /api/v1/state/{file_id} antes de adaptar o payload.
    let payloadToProcess = responseData;
    if (!hasResolvedPropertyPayload(payloadToProcess)) {
      if (acceptedFileId) {
        try {
          const stateResponse = await propertyApi.get(
            `/api/v1/state/${encodeURIComponent(String(acceptedFileId))}`,
          );
          if (stateResponse?.data) {
            payloadToProcess = stateResponse.data;
          }
        } catch (_) {
          // Mantém o payload original para erro explícito no adaptador estrito
        }
      }
    }

    // eslint-disable-next-line no-console
    if (__DEBUG) console.time('[Perf] Data.process');
    const propertyData = processarDadosPropriedade(payloadToProcess);
    // eslint-disable-next-line no-console
    if (__DEBUG) console.timeEnd('[Perf] Data.process');

    const file_id = propertyData?._metadata?.file_id || null;

    // Log detalhado da estrutura dos dados processados
    //console.log(`[PropertyService] Estrutura dos dados processados (${tipoConsulta}):`, {
    //  keys: Object.keys(propertyData || {}),
    //  hasCar: !!propertyData?.car,
    //  hasSigef: !!propertyData?.sigef,
    //  carType: propertyData?.car ? Array.isArray(propertyData.car) ? 'array' : typeof propertyData.car : 'não existe',
    //  sigefType: propertyData?.sigef ? Array.isArray(propertyData.sigef) ? 'array' : typeof propertyData.sigef : 'não existe',
    //  carLength: Array.isArray(propertyData?.car) ? propertyData.car.length : 'não é array',
    //  sigefLength: Array.isArray(propertyData?.sigef) ? propertyData.sigef.length : 'não é array'
    //});

    // Processa as geometrias usando o GeometryService - mesmo method para search e clique
    // eslint-disable-next-line no-console
    if (__DEBUG) console.time('[Perf] Geom.process');
    const geometryData = GeometryService.processPropertyGeometries(propertyData);
    // eslint-disable-next-line no-console
    if (__DEBUG) console.timeEnd('[Perf] Geom.process');

    //console.log(`[PropertyService] Geometrias processadas (${tipoConsulta}):`, {
    //  temas: Object.keys(geometryData.themeGeometries || {}),
    //  total: Object.values(geometryData.themeGeometries || {}).flat().length
    //});

    // Calcula centroide da propriedade se for busca por código
    let centroid = null;
    if (tipoConsulta === 'codigo' && geometryData?.geometries?.length > 0) {
      centroid = GeometryService.calculateCentroid(geometryData.geometries);
      //console.log(`[PropertyService] Centroide calculado: ${centroid ? JSON.stringify(centroid) : 'N/A'}`);
    }

    // Extrair informações dos códigos para metadata
    const extraData = {
      cod_imovel: params.cod_imovel || null,
      codigo_imo: params.codigo_imo || null,
      parcela_co: params.parcela_co || null,
      centroid: centroid,
      filename: file_id ? `${file_id}.json` : null, // Usa o file_id do backend e adiciona .json se tiver valor
      search_accepted_status: acceptedStatus,
      request_id: acceptedRequestId,
      job_id: acceptedJobId,
      accepted_file_id: acceptedFileId,
    };

    // Armazena hints de busca no _contextData
    try {
      const hints = {
        cod_imovel: params.cod_imovel || null,
        codigo_imo: params.codigo_imo || null,
        nirf: params.codigo_nirf || params.nirf || null,
        cpf: params.cpf || null,
        cnpj: params.cnpj || null,
      };
      propertyData._contextData = {
        ...(propertyData._contextData || {}),
        ...extraData,
        hints,
      };
    } catch (_) {
      /* no-op */
    }

    //console.log(`[PropertyService] Definindo filename no extraData como: ${extraData.filename}`);

    // Se fornecido um handler de resultado, usa-o para processar os dados
    if (typeof handlePropertyResult === 'function') {
      // Usa a mesma abordagem para busca e clique
      handlePropertyResult(propertyData, geometryData, extraData);
    } else if (setPropertyData) {
      // Definir os dados da propriedade no estado
      setPropertyData(propertyData);
    }

    // Streaming removido: não conectamos a eventos; UI é totalmente orientada ao snapshot retornado

    // Finaliza o estado de carregamento do request inicial
    if (setLoading) setLoading(false);

    // eslint-disable-next-line no-console
    if (__DEBUG) console.timeEnd('[Perf] Total.search->render');
    return propertyData;
  } catch (apiError) {
    try {
      const __DEBUG = import.meta.env.VITE_DEBUG === 'true';
      // eslint-disable-next-line no-console
      if (__DEBUG) console.timeEnd('[Perf] Total.search->render');
    } catch (_) {
      /* no-op */
    }
    // Log detalhado do erro para diagnóstico
    //console.error(`[PropertyService] Erro na requisição à API (${tipoConsulta}):`, {
    //  message: apiError.message,
    //  status: apiError.response?.status,
    //  statusText: apiError.response?.statusText,
    //  data: apiError.response?.data,
    //  headers: apiError.response?.headers,
    //  url: apiError.config?.url,
    //  method: apiError.config?.method,
    //  baseURL: apiError.config?.baseURL,
    //  params: apiError.config?.params
    //});

    // Verifica tipos específicos de erro
    if (apiError.message?.includes('Network Error')) {
      // eslint-disable-next-line preserve-caught-error
      throw new Error(
        'Erro de conexão com o servidor. Verifique sua internet ou se o servidor está online.',
      );
    } else if (apiError.response?.status === 401) {
      // eslint-disable-next-line preserve-caught-error
      throw new Error('Erro de autenticação. Por favor, faça login novamente.');
    } else if (apiError.response?.status === 403) {
      // eslint-disable-next-line preserve-caught-error
      throw new Error('Sem permissão para acessar este recurso.');
    } else if (apiError.response?.status === 404) {
      // Se o backend retornar JSON com message, é propriedade não encontrada (não erro técnico)
      const backendMessage = apiError.response?.data?.message;
      if (backendMessage) {
        // Retorna erro especial para o FE saber exibir marcador vermelho
        throw {
          isPropertyNotFound: true,
          message: backendMessage,
          searchParams: apiError.response?.data?.search_params || {},
          status: 404,
        };
      } else {
        // eslint-disable-next-line preserve-caught-error
        throw new Error('Endpoint não encontrado. Verifique a URL da API.');
      }
    } else if (apiError.response?.status >= 500) {
      // eslint-disable-next-line preserve-caught-error
      throw new Error(
        `Erro interno do servidor (${apiError.response?.status}). Por favor, tente novamente mais tarde.`,
      );
    }

    if (setLoading) setLoading(false);
    throw apiError;
  }
}

/**
 * Extraí códigos CAR da estrutura de dados da propriedade
 * @param {Object} data - Dados da propriedade
 * @returns {string|null} - Código CAR encontrado ou null
 */
export function getCARCodes(data) {
  const carList = data?.properties?.car;
  if (!Array.isArray(carList)) return null;

  const carItem = carList.find((item) => item?.cod_imovel);
  return carItem?.cod_imovel || null;
}

/**
 * Extraí códigos SIGEF da estrutura de dados da propriedade
 * @param {Object} data - Dados da propriedade
 * @returns {string|null} - Código SIGEF encontrado ou null
 */
export function getSIGEFCodes(data) {
  const sigefList = data?.properties?.sigef;
  if (!Array.isArray(sigefList)) return null;

  const sigefItem = sigefList.find((item) => item?.codigo_imo);
  return sigefItem?.codigo_imo || null;
}

// Processa os dados da propriedade
// @param {object} responseData - Dados recebidos da API
// @returns {object} - Dados processados
export function processarDadosPropriedade(responseData) {
  return adaptPropertyPayloadStrict(responseData);
}

// Exporta todas as funções necessárias
export default {
  buscarPorCoordenada,
  buscarPorCodigo,
  processarDadosPropriedade,
  getCARCodes,
  getSIGEFCodes,
};
